/**
 * Layout Actions
 *
 * Thin wrappers that delegate to CanvasEditor's layout methods.
 */

import type { CanvasEditor } from '@rool/editor';

/**
 * Apply auto layout to selected nodes.
 * Uses force2 for connected graphs, grid for disconnected.
 */
export function layoutSelection(editor: CanvasEditor): void {
	editor.layoutSelection({ minDistance: 30, algorithm: 'auto' });
}

/**
 * Apply force2 (organic) layout to selected nodes.
 */
export function layoutForce2(editor: CanvasEditor): void {
	editor.layoutSelection({ minDistance: 30, algorithm: 'force2' });
}

/**
 * Apply sort (masonry) layout to selected nodes.
 */
export function layoutSort(editor: CanvasEditor): void {
	editor.layoutSelection({ algorithm: 'sort' });
}

/**
 * Apply grid layout to selected nodes.
 */
export function layoutGrid(editor: CanvasEditor): void {
	editor.layoutSelection({ algorithm: 'grid' });
}

/**
 * Apply flow (DAG) layout to selected nodes.
 */
export function layoutFlow(editor: CanvasEditor): void {
	editor.layoutSelection({ algorithm: 'flow' });
}

/**
 * Apply tree layout to selected nodes.
 */
export function layoutTree(editor: CanvasEditor): void {
	editor.layoutSelection({ algorithm: 'tree' });
}

/**
 * Get label for layout command based on selection count.
 * When 0-1 nodes selected, layout operates on all nodes.
 */
export function getLayoutLabel(selectedCount: number): string | false {
	if (selectedCount < 2) return 'Auto-Layout All';
	return `Auto-Layout ${selectedCount} Nodes`;
}

// --- Align / Distribute ---

export function alignLeft(editor: CanvasEditor): void {
	editor.alignSelection('left');
}

export function alignCenter(editor: CanvasEditor): void {
	editor.alignSelection('center');
}

export function alignRight(editor: CanvasEditor): void {
	editor.alignSelection('right');
}

export function alignTop(editor: CanvasEditor): void {
	editor.alignSelection('top');
}

export function alignMiddle(editor: CanvasEditor): void {
	editor.alignSelection('middle');
}

export function alignBottom(editor: CanvasEditor): void {
	editor.alignSelection('bottom');
}

export function distributeHorizontal(editor: CanvasEditor): void {
	editor.alignSelection('horizontal');
}

export function distributeVertical(editor: CanvasEditor): void {
	editor.alignSelection('vertical');
}

/**
 * Get label for align commands. Requires 2+ selected nodes.
 */
export function getAlignLabel(label: string, selectedCount: number): string | false {
	if (selectedCount < 2) return false;
	return label;
}

/**
 * Get label for distribute commands. Requires 3+ selected nodes.
 */
export function getDistributeLabel(label: string, selectedCount: number): string | false {
	if (selectedCount < 3) return false;
	return label;
}
